import { useState, useEffect } from "react";
import { useSearchParams } from "react-router";
import { type Tab } from "~/components/common/OverflowTabs";
import { ShortcutTabs } from "~/components/common/ShortcutTabs";
import { DealFeed } from "~/components/common/DealFeed";
import { DealCard } from "~/components/common/DealCard";
import { DealCarouselSkeleton } from "~/components/skeleton/DealCarouselSkeleton";
import { RegularDealsStoreProvider, useRegularDealsStore } from "~/lib/stores/regular-deals";
import { Card, CardContent } from "~/components/ui/card";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "~/components/ui/carousel";
import PageContainer from "~/components/common/PageContainer";
import Footer from "~/components/common/Footer";
import { devLog } from "~/lib/dev-log";
import { buildShopChannel } from "~/lib/deal-channel";
import { useFetchOnce } from "~/lib/hooks/useFetchOnce";
import Sidebar from "./sidebar";
import { Banner1, Banner2 } from "./banner";
import Trending from "./trending/trending";
import { useUserStore } from "~/lib/stores/user";
import { AnalyticsEventBuilder } from "~/lib/analytics";
import { saveAnalyticsContext } from "~/lib/analytics-context";
import { ROOT_CATEGORY_CONFIG } from "~/lib/category";
import SubcategoryScroll from "~/components/common/SubcategoryScroll";
import CategoryContentWithSections from "~/components/common/CategoryContentWithSections";
import BlidzPlusBadge from "~/components/common/BlidzPlusBadge";
import { VideoDealsSection } from "~/components/common/VideoDealsSection";
import { CinemaSection } from "~/components/common/cinema/CinemaSection";
import { DEAL_FEED_AD_INTERVAL } from "~/lib/ads";
import { useIsMobile } from "~/lib/hooks/useIsMobile";
import bannerBkgImg from "~/assets/deal-feed/banner_bkg.svg";
import type { Route } from "./+types/deal-feeds";

// Preload the homepage banner background (applied via CSS background-image in
// Banner1, which the browser would otherwise discover late). Scoped to this
// route so it does not load on other pages.
export const links: Route.LinksFunction = () => [
  {
    rel: "preload",
    href: bannerBkgImg,
    as: "image",
    type: "image/svg+xml",
    fetchPriority: "high",
  } as any,
];

// Toggle constants for controlling visibility of shop page elements
// Set to false to hide the element, true to show
const SHOW_APP_DOWNLOAD_BANNER = true; // Banner1: "GET BLIDZ APP NOW" / "Exclusive app benefits"
const SHOW_SIDEBAR = true; // Sidebar: "Blidz Plus Benefits" with payout options

// Fixed tabs for OverflowTabs
export const FIXED_TABS: Tab[] = ROOT_CATEGORY_CONFIG.map(({ id, label }) => ({ id, label }));

// Component for each tab content that uses the store
interface TabContentProps {
  tabId: string;
  isActive: boolean;
  entryPoint?: string;
  showTopPicks?: boolean;
  showTrending?: boolean;
  showRecommend?: boolean;
  showTitle?: boolean;
}

export function TabContent({
  tabId,
  isActive,
  entryPoint,
  showTopPicks = true,
  showTrending = true,
  showRecommend = true,
  showTitle = true,
}: TabContentProps) {
  const { deals, isLoading, fetchDeals, fetchMoreDeals, hasMore } = useRegularDealsStore();
  const user = useUserStore(state => state.user);
  const isMobile = useIsMobile();

  // Build channel string for analytics
  const channelString = JSON.stringify(buildShopChannel(tabId));

  // Fetch deals only when tab becomes active and no deals are loaded yet
  useFetchOnce(
    isActive && deals.length === 0 ? `${tabId}|${entryPoint ?? ""}` : null,
    () => {
      const config = ROOT_CATEGORY_CONFIG.find(tab => tab.id === tabId);
      const configEntryPoint = config ? config.entrypoint : tabId;
      const channel = buildShopChannel(tabId);
      fetchDeals(entryPoint || configEntryPoint, undefined, channel);
    },
    !isLoading
  );

  // Handle load more deals
  const handleLoadMore = () => {
    if (hasMore && !isLoading) {
      const channel = buildShopChannel(tabId);
      fetchMoreDeals(channel);
    }
  };

  // Split deals into Best sell (first 10) and Recommend (rest)
  const topPickDeals = showTopPicks ? deals.slice(0, 10) : [];
  const recommendDeals = showRecommend ? deals.slice(10) : [];

  const topPickContent = (
    <div className="group">
      <Carousel
        opts={{
          align: "start",
          loop: false,
          dragFree: true,
          containScroll: "trimSnaps",
          duration: 20,
          watchResize: false,
          watchSlides: false,
        }}
        className="relative w-full"
      >
        <CarouselContent className="-ml-2 md:-ml-4">
          {topPickDeals.map((deal, index) => (
            <CarouselItem key={deal._id} className="basis-1/2 pl-2 md:basis-1/3 md:pl-4 lg:basis-1/4 xl:basis-1/5">
              <DealCard
                deal={deal}
                badgeText={`No.${index + 1}`}
                channel={channelString}
                position={index}
                onClick={async () => {
                  // Save analytics context before navigation
                  if (user) {
                    const contextAttrs = AnalyticsEventBuilder.createContextAttrs(channelString, index);
                    const itemAttrs = AnalyticsEventBuilder.createItemAttrs({
                      currency: user.currency || "USD",
                      price_dash: deal.price || 0,
                    });

                    await saveAnalyticsContext(deal._id, deal._productId, deal.reqId || "", contextAttrs, itemAttrs);
                  }

                  // Build URL with reqId if available
                  let url = `/deal/detail?deal_id=${deal._id}`;
                  if (deal.reqId) {
                    url += `&req_id=${deal.reqId}`;
                  }
                  // open in new tab
                  window.open(url, "_blank");
                }}
              />
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious
          variant="carouselNav"
          size="icon"
          customPosition={true}
          className="absolute top-1/3 -left-4 z-10 inline-flex size-8 -translate-y-1/2 disabled:hidden"
        />
        <CarouselNext
          variant="carouselNav"
          size="icon"
          customPosition={true}
          className="absolute top-1/3 -right-4 z-10 inline-flex size-8 -translate-y-1/2 disabled:hidden"
        />
      </Carousel>
    </div>
  );

  return (
    <div id={`deal-tab-content-${tabId}`} className="space-y-10">
      {/* Top picks Section */}
      <section id={`deal-tab-top-picks-${tabId}`}>
        {showTitle && (
          <h2
            id={`deal-tab-top-picks-title-${tabId}`}
            className="mb-4 flex items-center gap-4 text-3xl font-bold tracking-tight"
          >
            Top picks
            <BlidzPlusBadge text="Plus Member price" />
          </h2>
        )}
        {isLoading || topPickDeals.length === 0 ? <DealCarouselSkeleton /> : topPickContent}
      </section>

      {/* Cinema Section */}
      <CinemaSection />

      {/* Video Deals Section */}
      <VideoDealsSection />

      {/* Trending Section */}
      {showTrending && (
        <section id={`deal-tab-trending-${tabId}`} className="hidden lg:block">
          {showTitle && (
            <h2
              id={`deal-tab-trending-title-${tabId}`}
              className="mb-4 flex items-center gap-4 text-3xl font-bold tracking-tight"
            >
              Trending
              <BlidzPlusBadge text="Plus Member price" />
            </h2>
          )}
          <Trending tabId={tabId} />
        </section>
      )}

      {/* Recommend Section */}
      <section id={`deal-tab-recommended-${tabId}`}>
        {showTitle && (
          <h2
            id={`deal-tab-recommended-title-${tabId}`}
            className="mb-4 flex items-center gap-4 text-3xl font-bold tracking-tight"
          >
            Recommended
            <BlidzPlusBadge text="Plus Member price" />
          </h2>
        )}
        <DealFeed
          deals={recommendDeals}
          onLoadMore={handleLoadMore}
          isLoading={isLoading}
          hasMore={hasMore}
          dealPerRow={{
            default: 2,
            mobile: 2,
            tablet: 4,
            desktopSmall: 4,
            desktopLarge: 5,
          }}
          channel={channelString}
          adInterval={isMobile ? DEAL_FEED_AD_INTERVAL.mobile : DEAL_FEED_AD_INTERVAL.desktop}
        />
      </section>
    </div>
  );
}

export default function DealFeeds() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(FIXED_TABS[0].id);

  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam && FIXED_TABS.some(tab => tab.id === tabParam)) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  const rootCategory = ROOT_CATEGORY_CONFIG.find(tab => tab.id === activeTab);
  const categoryEntrypoint = rootCategory?.entrypoint!;

  const handleTabChange = (tabId: string) => {
    devLog("Tab clicked:", tabId);
    setActiveTab(tabId);
    // update URL search param
    setSearchParams(prevParams => {
      const params = new URLSearchParams(prevParams);
      params.set("tab", tabId);
      return params;
    });
  };

  return (
    <PageContainer headerProps={{ showBlidzAppMenu: true, showModeSwitch: true }}>
      {/* Shortcut tabs - fixed below header */}
      <ShortcutTabs tabs={FIXED_TABS} activeTabId={activeTab} onTabChange={handleTabChange} />

      {/* Main content with padding for shortcut tabs */}
      {rootCategory?.hasSubcategories ? (
        <GoogleCategoryContent categoryEntrypoint={categoryEntrypoint} activeTab={activeTab} />
      ) : (
        <BlidzCategoryContent activeTab={activeTab} />
      )}
      <Footer />
    </PageContainer>
  );
}

interface BlidzCategoryContentProps {
  activeTab: string;
}

function BlidzCategoryContent({ activeTab }: BlidzCategoryContentProps) {
  return (
    <div className="pt-[44px]">
      <div id="deal-feeds-banner" className="mx-[16px] flex flex-col gap-[16px] pt-[16px]">
        {SHOW_APP_DOWNLOAD_BANNER && <Banner1 />}
        <Banner2 />
      </div>
      <div id="deal-feeds-container" className="mx-auto flex w-full max-w-[1392px] flex-col gap-[16px] pt-[16px]">
        <div id="deal-feeds-main-content" className="mx-[16px] mb-[16px] flex items-start justify-center gap-[24px]">
          <Card
            id="deal-feeds-card"
            className="max-w-[1086px] min-w-0 flex-1 rounded-[24px] border-none p-[24px] shadow-none"
          >
            <CardContent id="deal-feeds-card-content" className="px-0">
              <RegularDealsStoreProvider channelId={activeTab}>
                <TabContent tabId={activeTab} isActive={true} />
              </RegularDealsStoreProvider>
            </CardContent>
          </Card>
          {SHOW_SIDEBAR && (
            <div id="deal-feeds-sidebar" className="hidden w-full lg:block lg:w-[222px]">
              <Sidebar />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface GoogleCategoryContentProps extends BlidzCategoryContentProps {
  categoryEntrypoint?: string;
}

function GoogleCategoryContent({ categoryEntrypoint, activeTab }: GoogleCategoryContentProps) {
  const rootCategory = ROOT_CATEGORY_CONFIG.find(tab => tab.id === activeTab);
  const rootCategoryId = rootCategory?.entrypoint!;

  return (
    <div className="pt-[64px]">
      <div className="mx-auto px-[24px]">
        <SubcategoryScroll rootCategoryId={rootCategoryId} subCategoryId={categoryEntrypoint} />
      </div>

      {/* Main content area */}
      <div className="mx-auto w-full max-w-[1392px] px-[24px]">
        <div className="flex items-start justify-center gap-[24px] py-[24px]">
          {/* Left: Product lists - 1147px */}
          <div className="max-w-[1147px] min-w-0 flex-1">
            <CategoryContentWithSections
              rootCategoryId={rootCategoryId}
              subCategoryId={categoryEntrypoint}
              tabId={activeTab}
            />
          </div>

          {/* Right: Sidebar - 221px */}
          {SHOW_SIDEBAR && (
            <div className="hidden w-[221px] shrink-0 lg:block">
              <Sidebar />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
